import React, { createContext, useContext, useState, useEffect } from 'react';
import api from './api';
import { resolveTenantSlug } from './branding';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('gymify_token');
    const storedUser = localStorage.getItem('gymify_user');
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const tenantSlug = resolveTenantSlug();
      const response = await api.post('/auth/login', {
        email,
        password,
        tenant_slug: tenantSlug,
      });
      const { access_token, refresh_token, user: userData } = response.data;

      localStorage.setItem('gymify_token', access_token);
      localStorage.setItem('gymify_user', JSON.stringify(userData));
      if (refresh_token) {
        localStorage.setItem('gymify_refresh', refresh_token);
      } else {
        localStorage.removeItem('gymify_refresh');
      }
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('gymify_token');
    localStorage.removeItem('gymify_user');
    localStorage.removeItem('gymify_refresh');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/auth/me');
      localStorage.setItem('gymify_user', JSON.stringify(data));
      setUser(data);
      return data;
    } catch (error) {
      return null;
    }
  };

  // Superadmin impersonation: swap the token for one scoped to the target
  // tenant, then hard-reload so tenant-scoped data and branding refetch
  // consistently (mirrors the SchemePickerDialog reload pattern).
  const actAs = async (tenantId) => {
    const { data } = await api.post('/auth/act-as', { tenant_id: tenantId });
    localStorage.setItem('gymify_token', data.access_token);
    localStorage.setItem('gymify_user', JSON.stringify(data.user));
    // Keep branding (fetched by slug) in sync with the impersonated tenant.
    if (data.user?.tenant_slug) localStorage.setItem('tenant_slug', data.user.tenant_slug);
    window.location.reload();
  };

  const exitActAs = async () => {
    const { data } = await api.post('/auth/exit-act-as');
    localStorage.setItem('gymify_token', data.access_token);
    localStorage.setItem('gymify_user', JSON.stringify(data.user));
    if (data.user?.tenant_slug) localStorage.setItem('tenant_slug', data.user.tenant_slug);
    window.location.reload();
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    actAs,
    exitActAs,
    isAuthenticated: !!user,
    isSuperadmin: user?.role === 'superadmin',
    // Owners: tenant owner_admin plus superadmin. Gates bulk data exports
    // and public-form link management; staff are deliberately excluded.
    isOwner: user?.role === 'owner_admin' || user?.role === 'superadmin',
    isAdmin: user?.role === 'owner_admin' || user?.role === 'staff' || user?.role === 'superadmin',
    isTrainer: user?.role === 'trainer',
    isMember: user?.role === 'member',
    isActingAs: !!user?.act_as_tenant_id,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};